import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { useMemo } from 'react';

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const COLORS = { best: '#10b981', good: '#6ee7b7', avg: '#d1d5db', low: '#fca5a5', worst: '#ef4444' };

export default function DayOfWeekPatterns({ individualTrends }) {
  const patterns = useMemo(() => {
    if (!individualTrends || individualTrends.length < 7) return [];
    const buckets = Array.from({ length: 7 }, () => ({ mood: [], energy: [], stress: [], perf: [] }));

    individualTrends.forEach(d => {
      const dayIdx = new Date(d.date).getDay();
      buckets[dayIdx].mood.push(d.mood || 0);
      buckets[dayIdx].energy.push(d.energy || 0);
      buckets[dayIdx].stress.push(d.stress || 0);
      buckets[dayIdx].perf.push(d.performance_rating || 0);
    });

    const result = buckets.map((b, i) => {
      const avg = (arr) => arr.length ? +(arr.reduce((s, v) => s + v, 0) / arr.length).toFixed(1) : 0;
      const wellness = avg(b.mood) + avg(b.energy) + avg(b.perf) - avg(b.stress);
      return { day: DAY_NAMES[i], mood: avg(b.mood), energy: avg(b.energy), stress: avg(b.stress), performance: avg(b.perf), wellness: +wellness.toFixed(1), count: b.mood.length };
    });

    const maxW = Math.max(...result.map(r => r.wellness));
    const minW = Math.min(...result.map(r => r.wellness));
    result.forEach(r => {
      if (r.wellness === maxW) r.tier = 'best';
      else if (r.wellness === minW) r.tier = 'worst';
      else if (r.wellness >= (maxW + minW) / 2 + (maxW - minW) * 0.15) r.tier = 'good';
      else if (r.wellness <= (maxW + minW) / 2 - (maxW - minW) * 0.15) r.tier = 'low';
      else r.tier = 'avg';
    });

    return result;
  }, [individualTrends]);

  if (patterns.length === 0) return null;

  const bestDay = patterns.reduce((a, b) => a.wellness > b.wellness ? a : b);
  const worstDay = patterns.reduce((a, b) => a.wellness < b.wellness ? a : b);

  return (
    <div data-testid="day-of-week-patterns" className="bg-white rounded-xl shadow-lg p-6 border border-emerald-100">
      <h2 className="text-lg font-bold text-gray-900 mb-1">Day-of-Week Patterns</h2>
      <p className="text-xs text-gray-500 mb-4">
        Your best day tends to be <strong className="text-emerald-600">{bestDay.day}</strong> and toughest is <strong className="text-red-500">{worstDay.day}</strong>
      </p>
      <ResponsiveContainer width="100%" height={220}>
        <BarChart data={patterns} barCategoryGap="15%">
          <CartesianGrid strokeDasharray="3 3" vertical={false} />
          <XAxis dataKey="day" tick={{ fontSize: 12, fontWeight: 600 }} />
          <YAxis hide domain={['auto', 'auto']} />
          <Tooltip content={({ active, payload }) => {
            if (!active || !payload?.length) return null;
            const d = payload[0].payload;
            return (
              <div className="bg-white rounded-lg shadow-lg border px-3 py-2 text-xs">
                <p className="font-bold">{d.day} ({d.count} check-ins)</p>
                <p>Mood: {d.mood} | Energy: {d.energy}</p>
                <p>Focus: {d.performance} | Stress: {d.stress}</p>
                <p className="font-semibold mt-1">Wellness Score: {d.wellness}</p>
              </div>
            );
          }} />
          <Bar dataKey="wellness" radius={[6, 6, 0, 0]}>
            {patterns.map((entry, idx) => (
              <Cell key={idx} fill={COLORS[entry.tier]} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
      <div className="flex justify-center gap-4 mt-2 text-[10px] text-gray-500">
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-500" /> Best</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-300" /> Good</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-gray-300" /> Average</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-300" /> Below Avg</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-500" /> Toughest</span>
      </div>
    </div>
  );
}
